import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useTranslation } from '../../i18n/LanguageProvider';
import { authorList, publicationKeywords, localizedAbstract, localizedTitle, originalTitle, publicationLanguage, publicationLanguages, publicationYear } from './publicationTypes';

const PublicationReference = lazy(() => import('./PublicationReference'));

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return isMobile;
}

// Everything the entry offers besides its text: the paper, the slides, the recording, and any
// extra item declared in `other`.
function assetsOf(entry, basePath, t) {
  const assets = [];

  if (entry.paperPdf) {
    assets.push({ key: 'paper', label: t('publications.paper'), kind: 'pdf', view: 'FitH', href: `/${basePath}/papers/${entry.paperPdf}` });
  }
  if (entry.slidesPDF) {
    assets.push({ key: 'slides', label: t('publications.slides'), kind: 'pdf', view: 'Fit', href: `/${basePath}/presentations/${entry.slidesPDF}` });
  } else if (entry.canvaPresentation) {
    assets.push({ key: 'slides', label: t('publications.slides'), kind: 'embed', href: entry.canvaPresentation });
  }
  if (entry.youtubeLink) {
    assets.push({ key: 'video', label: t('publications.recording'), kind: 'embed', href: entry.youtubeLink });
  }

  (Array.isArray(entry.other) ? entry.other : []).forEach((item, index) => {
    const pdf = ['pdf', 'file', 'paperPdf', 'slidesPDF', 'posterPdf'].find(field => item[field]);
    const url = ['url', 'link', 'youtubeLink', 'canvaPresentation', 'embed'].find(field => item[field]);
    const label = item.label || item.title || `${t('publications.info')} ${index + 1}`;

    if (pdf) assets.push({ key: `other-${index}`, label, kind: 'pdf', view: 'FitH', href: `/${basePath}/other/${item[pdf]}` });
    else if (url) {
      const embeddable = ['youtubeLink', 'canvaPresentation', 'embed'].includes(url);
      assets.push({ key: `other-${index}`, label, kind: embeddable ? 'embed' : 'link', href: item[url] });
    }
  });

  return assets;
}

// A deck asks for the whole page so no slide is clipped; a paper asks for the width, which is
// how one reads a column of text. Firefox, Safari and Adobe honour these; Chrome's viewer
// ignores them and picks its own fit, which still shows a full slide at this frame size.
function AssetViewer({ asset, title, isMobile, t }) {
  if (asset.kind === 'link') {
    return <p className="entry-asset-fallback">
      <a href={asset.href} target="_blank" rel="noreferrer">{t('publications.openLink')}</a>
    </p>;
  }

  if (asset.kind === 'pdf' && isMobile) {
    return <p className="entry-asset-fallback">
      {t('publications.mobilePdf')}{' '}
      <a href={asset.href} target="_blank" rel="noreferrer">{t('publications.openPdf')}</a>
    </p>;
  }

  return <div className="entry-asset-frame" data-kind={asset.kind}>
    <iframe
      title={`${asset.label} — ${title}`}
      src={asset.kind === 'pdf' ? `${asset.href}#view=${asset.view}` : asset.href}
      loading="lazy"
      allow="autoplay; encrypted-media"
      allowFullScreen
    />
  </div>;
}

export default function PublicationDetail({ entry, basePath, backTo, backLabel, formatLabel, showReference = true }) {
  const { isPortuguese, t } = useTranslation();
  const text = (pt, en) => isPortuguese ? pt : en;
  const isMobile = useIsMobile();
  // Where the reader came from, filters and all; empty when the page was opened directly.
  const { state } = useLocation();
  const backSearch = typeof state?.list === 'string' ? state.list : '';
  const assets = useMemo(() => assetsOf(entry, basePath, t), [entry, basePath, t]);
  const [selected, setSelected] = useState(assets[0]?.key);

  useEffect(() => { setSelected(assetsOf(entry, basePath, t)[0]?.key); }, [entry, basePath, t]);

  const asset = assets.find(item => item.key === selected) || assets[0];
  const language = publicationLanguages.find(([key]) => key === publicationLanguage(entry));
  const venue = entry['container-title'] || entry['event-title'] || entry.publisher;
  const place = entry['publisher-place'] || entry['event-place'];
  const keywords = publicationKeywords(entry, isPortuguese);
  const title = localizedTitle(entry, isPortuguese);
  const published = originalTitle(entry, isPortuguese);
  const abstract = localizedAbstract(entry, isPortuguese);

  return (
    <article className="entry-detail">
      <p className="entry-back"><Link to={{ pathname: backTo, search: backSearch }}>← {backLabel}</Link></p>

      {entry.award && <p className="publication-award-callout">{text(entry.awardPt, entry.award)}</p>}
      <div className="publication-overline">
        <span>{publicationYear(entry)}</span>
        {formatLabel && <span className="publication-format-label">{formatLabel}</span>}
        <span className="publication-language-label">{text(language[1], language[2])}</span>
      </div>

      <h1 className="entry-title">{title}</h1>
      {published && <p className="entry-original-title"><span>Published as</span> <span lang="pt-BR">{published}</span></p>}
      <p className="entry-authors">{authorList(entry.author)}</p>
      {venue && <p className="entry-venue">{[venue, place].filter(Boolean).join(' · ')}</p>}

      {abstract && (
        <section className="entry-abstract" aria-labelledby="entry-abstract-heading">
          <h2 id="entry-abstract-heading">{t('publications.abstract')}</h2>
          <p>{abstract}</p>
        </section>
      )}

      {keywords.length > 0 && (
        <p className="entry-keywords"><strong>{t('common.keywords')}:</strong> {keywords.join(', ')}</p>
      )}

      {assets.length > 0 && (
        <section className="entry-assets" aria-labelledby="entry-assets-heading">
          <h2 id="entry-assets-heading">{text('Materiais', 'Materials')}</h2>
          {assets.length > 1 && (
            <div className="entry-asset-tabs" role="tablist">
              {assets.map(item => (
                <button
                  key={item.key}
                  type="button"
                  role="tab"
                  aria-selected={item.key === asset?.key}
                  onClick={() => setSelected(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
          {asset && <AssetViewer asset={asset} title={entry.title} isMobile={isMobile} t={t} />}
        </section>
      )}

      {showReference && (
        <Suspense fallback={<p className="entry-reference-loading">{t('publications.reference')}…</p>}>
          <PublicationReference entry={entry} />
        </Suspense>
      )}
    </article>
  );
}
